<script setup lang="ts">
import { useLanguage } from '#composables/useLanguage';
import { GRANULARITY_FIELD } from '#stores/utils/filterMapping/constants/filterMapping.constant';
import { C4ButtonBase } from '@c4-links/vue3-links-components';
import { FilterDefinitionFormType } from '@carrefour-gcs/bff-sdk';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import DynamicFormField from './DynamicFormField.vue';
import { usePeriodReferentialIntervalResolver } from './fields/PeriodField/composables/usePeriodReferentialInterval';
import type { DynamicFormPropsType } from './types/dynamicFormProps.type';
import type { FieldDefinitionType } from './types/field.type';
import { createSchema, resolveDateBoundary } from './utils/dynamicFormSchema.utils.js';
import { resolveFilterValueRestriction } from './utils/filterRestriction.utils';

const { fields, isActive = true } = defineProps<DynamicFormPropsType>();
const orderedFields = computed(() => [...fields].sort((a, b) => a.order - b.order));

// Template refs for checkbox tree fields to reset selections on cancel
const formFieldRefs = ref<InstanceType<typeof DynamicFormField>[]>([]);

const emit = defineEmits<{
  (e: 'submit', payload: Record<string, unknown>): void;
  (e: 'cancel'): void;
}>();

const { t } = useLanguage();
const getReferentialInterval = usePeriodReferentialIntervalResolver();
const { handleSubmit, values, setFieldValue } = useForm({
  validationSchema: toTypedSchema(createSchema(fields, { getReferentialInterval })),
  keepValuesOnUnmount: false,
  validateOnMount: false
});

const granularity = computed(() => values[GRANULARITY_FIELD]);

/**
 * Latest date a field may start at, recomputed as its reference field changes so the picker
 * always disables what the schema would reject.
 */
const getMaxDate = (field: FieldDefinitionType) => resolveDateBoundary(field, values, getReferentialInterval);

const shouldShowField = (field: FieldDefinitionType): boolean => {
  if (!field.dependency) {
    return true;
  }

  const parentValue = values[field.dependency];

  if (!field.dependencyValue && parentValue) {
    return true;
  }

  return parentValue === field.dependencyValue;
};

// Watch for changes in values and reset dependent fields
watch(
  () => ({ ...values }),
  (newValues, oldValues) => {
    fields
      .filter((field) => Boolean(field.dependency))
      .forEach((field) => {
        const parentFieldName = field.dependency as string;

        if (newValues[parentFieldName] !== oldValues[parentFieldName]) {
          setFieldValue(field.fieldName, undefined);
        }
      });
  },
  { deep: true }
);

/**
 * Values of a field that its restrictions disable, given the current form values.
 */
const getDisabledValues = (field: FieldDefinitionType): string[] =>
  (field.options ?? [])
    .filter((option) => resolveFilterValueRestriction(option.restrictions, values).disabled)
    .map((option) => option.value);

/**
 * Drops the selected values of a field that became disabled. A single choice field falls back
 * on its first enabled option, a multiple choice field only loses the disabled values.
 */
const dropDisabledValues = (field: FieldDefinitionType) => {
  const disabledValues = getDisabledValues(field);
  const currentValue = values[field.fieldName];

  if (disabledValues.length === 0) {
    return;
  }

  if (Array.isArray(currentValue)) {
    const enabledValues = currentValue.filter((value) => !disabledValues.includes(String(value)));

    if (enabledValues.length !== currentValue.length) {
      setFieldValue(field.fieldName, enabledValues);
    }

    return;
  }

  if (typeof currentValue === 'string' && disabledValues.includes(currentValue)) {
    const fallbackOption = field.options?.find((option) => !disabledValues.includes(option.value));
    setFieldValue(field.fieldName, fallbackOption?.value);
  }
};

const restrictedFields = computed(() =>
  orderedFields.value.filter((field) => field.options?.some((option) => option.restrictions?.length))
);

// A selected value disabled by the selection of another field is dropped, so the form never holds a
// combination the restrictions forbid (e.g. like for like with e-commerce for ESP / BEL).
watch(
  () => ({ ...values }),
  () => restrictedFields.value.forEach(dropDisabledValues),
  { deep: true, immediate: true }
);

const dividerClass = computed(() => {
  const hasCheckboxTree = fields.some((field) => field.element === FilterDefinitionFormType.CHECKBOX_TREE);
  return hasCheckboxTree ? '' : 'my-2';
});

const isDividerVisible = (index: number): boolean => {
  return (
    orderedFields.value[index + 1]?.element !== FilterDefinitionFormType.CHECKBOX_TREE &&
    orderedFields.value[index + 1]?.element !== FilterDefinitionFormType.CHECKBOX_GROUP &&
    orderedFields.value[index + 1]?.element !== FilterDefinitionFormType.CHECKBOX_GRID
  );
};

const onSubmit = handleSubmit((data) => {
  emit('submit', data);
});

const onCancel = () => {
  // Reset all checkbox tree selections in the form fields
  formFieldRefs.value.forEach((fieldRef) => {
    if (fieldRef?.resetCheckboxTreeSelections) {
      fieldRef.resetCheckboxTreeSelections();
    }
  });

  emit('cancel');
};
</script>

<template>
  <div>
    <div v-for="(field, index) in orderedFields" :key="field.fieldName">
      <DynamicFormField
        v-if="shouldShowField(field)"
        ref="formFieldRefs"
        :field="field"
        :max-date="getMaxDate(field)"
        :granularity="granularity"
        :form-values="values"
      />
      <hr v-if="isDividerVisible(index)" :class="dividerClass" />
    </div>

    <Teleport v-if="isActive" defer to="#drawer-button-bar">
      <div class="p-3 w-full h-15 flex gap-x-3">
        <C4ButtonBase class="flex-1" variation="secondary" :label="t('filters.form.button.cancel')" @click="onCancel" />
        <C4ButtonBase class="flex-1" :label="t('filters.form.button.apply')" @click="onSubmit" />
      </div>
    </Teleport>
  </div>
</template>
